/**
 * @poc-to-production
 *
 * Curated Los Angeles County plan-submittal document checklist for SR Alpha.
 * Source link is the official county permitting entry point — catalog rows
 * are maintained in-repo, not scraped at runtime.
 */

export interface SubmissionDocRequirement {
  id: string;
  label: string;
  required: boolean;
  /** Lowercase keywords matched against normalized file names. */
  matchKeywords: string[];
  /** Plain-language criteria sent to AIS before ACC upload. */
  validationCriteria: string;
  note?: string;
}

export interface SubmissionChecklistCatalog {
  id: string;
  permittingAuthorityLabel: string;
  codeBasisHint: string;
  officialSourceUrl: string;
  officialSourceLabel: string;
  residentialChecklistPdfUrl?: string;
  residentialChecklistPdfLabel?: string;
  requirements: SubmissionDocRequirement[];
}

export const LA_COUNTY_SUBMISSION_CATALOG: SubmissionChecklistCatalog = {
  id: "us-ca-la-county",
  permittingAuthorityLabel:
    "Los Angeles County — Department of Public Works (Building & Safety)",
  codeBasisHint:
    "2022 California Residential Code (Title 24 Part 2.5), LA County amendments",
  officialSourceUrl:
    "https://dpw.lacounty.gov/building-and-safety/plancheck/building",
  officialSourceLabel: "LACO DPW — Building plan check",
  residentialChecklistPdfUrl:
    "https://dpw.lacounty.gov/bsd/lib/fp/Building/Residential/Residential%20Minimum%20Plan%20Submittal%20Requirements.pdf",
  residentialChecklistPdfLabel: "Residential minimum plan submittal (PDF)",
  requirements: [
    {
      id: "cover-sheet",
      label: "Cover sheet / title block",
      required: true,
      matchKeywords: ["cover", "title", "index", "sheet index"],
      validationCriteria:
        "Architectural cover sheet or title block listing project address, sheet index, and design professional information.",
    },
    {
      id: "site-plan",
      label: "Site plan",
      required: true,
      matchKeywords: ["site", "plot", "survey"],
      validationCriteria:
        "Site or plot plan showing property lines, building footprint, setbacks, and north arrow.",
    },
    {
      id: "floor-plan",
      label: "Floor plan(s)",
      required: true,
      matchKeywords: ["floor", "plan", "a1", "a-1"],
      validationCriteria:
        "Floor plan drawing(s) with room labels, dimensions, and door/window locations.",
    },
    {
      id: "elevations",
      label: "Exterior elevations",
      required: true,
      matchKeywords: ["elevation", "elev"],
      validationCriteria:
        "Exterior building elevations with heights and finish materials noted.",
    },
    {
      id: "sections",
      label: "Building sections",
      required: true,
      matchKeywords: ["section", "sect"],
      validationCriteria:
        "Building section(s) cutting through the structure showing floor-to-floor heights and roof assembly.",
    },
    {
      id: "roof-plan",
      label: "Roof plan",
      required: true,
      matchKeywords: ["roof"],
      validationCriteria:
        "Roof plan showing ridges, slopes, drainage, and equipment locations if any.",
    },
    {
      id: "energy",
      label: "Energy compliance (CF1R / Title 24)",
      required: true,
      matchKeywords: ["energy", "cf1r", "title24", "title-24", "t24"],
      note: "Often a separate PDF from the architect or energy consultant.",
      validationCriteria:
        "California Title 24 energy compliance documentation (CF1R or equivalent compliance forms).",
    },
    {
      id: "structural",
      label: "Structural calculations / sheets",
      required: false,
      matchKeywords: ["struct", "calc", "s1", "foundation"],
      validationCriteria:
        "Structural sheets or calculation package for foundations, framing, or lateral system.",
    },
  ],
};
